import moment from 'moment/moment';
import 'moment/locale/he';
import { Box, Checkbox, FormControlLabel, FormGroup, Grid, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { Moment } from 'moment';
import CalendarDay from '../CalendarDay';
import { CalendarItems, DATE_FORMAT } from '../calendarWrapper/CalendarWrapper';
import { AllDayCalendarCell } from '../AllDayCalendarCell';
import { CurrentTimeDivider } from './components/CurrentTimeDivider';
import { useAuth } from '../../session/InternalAuthProvider';
import { getHoliday } from '../../common/getHoliday';
import { useCalendarLocalPreferences } from './useCalendarLocalPreferences';
import { useLanguage } from '../../common/GeneralUtils';
import { AccountData, EnrichedEventData, OrderData, WaitingListStatus } from '../../api';
import { UseAuthPermissions } from '../../session/UseAuthPermissions';
import { WaitingListIndicator } from './components/WaitingListIndicator';
import { ResourceHeaders } from '../calendar-items/ResourceHeaders';
import { calculateCapacityStats } from '../utils/capacityCalculationUtils';
import CapacityStatsGrid from '../components/CapacityStatsGrid';
import { useCalendar } from '../CalendarContext/CalendarContext';
import { HoursBordersHeader } from './components/HoursBordersHeader';

const HEADER_FORMAT_SMALL = 'ddd DD';
type MultipleDaysViewProps = {
  currentDate: string;
  calendarItems: CalendarItems;
  numOfDays: number;
  openOrder: (event: any, order: OrderData) => void;
  openEvent: (event: EnrichedEventData) => void;
};

const MultipleDaysView = ({ currentDate, calendarItems, numOfDays, openOrder, openEvent }: MultipleDaysViewProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { readOnly } = UseAuthPermissions();
  const { calendarView } = useCalendar();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const today = moment().startOf('day').format(DATE_FORMAT);
  const {
    preferences,
    toggleShowUnavailability,
    toggleShouldShowOnlyCurrentUser,
    toggleShouldShowShifts,
    toggleShowUserUnavailability,
  } = useCalendarLocalPreferences();

  const hasSchedulerConfigs = (account?.shared_resources?.scheduler_configs?.length || 0) > 0;
  const shouldShowResources = hasSchedulerConfigs && numOfDays === 1;

  const capacityStats = useMemo(
    () => calculateCapacityStats(calendarItems, account, currentDate, numOfDays),
    [calendarItems, account, currentDate, numOfDays],
  );

  useEffect(() => {
    const lastScrollPosition = localStorage.getItem('webook_lastCalendarScroll');
    // @ts-ignore
    document.getElementById('calendar-container').scrollTo({ top: lastScrollPosition });
  }, []);
  const handleScroll = (event: any) => {
    localStorage.setItem('webook_lastCalendarScroll', event.currentTarget.scrollTop);
  };

  const isNumOfDayToday = (numOfDay: number) => moment(currentDate).add(numOfDay, 'days').format(DATE_FORMAT) === today;
  const cellBackgroundColor = (date: Moment) => {
    if (numOfDays === 7) {
      return date.weekday() % 2 === 0 ? 'white' : '#f8f8f8';
    }
    if (numOfDays === 1) {
      return 'white';
    }
    const formattedDate = date.subtract(1, 'day').format(DATE_FORMAT);
    return formattedDate === currentDate ? '#f8f8f8' : 'white';
  };

  return (
    <>
      <Grid container justifyContent="space-between">
        <FormGroup>
          <Grid
            container
            border={'1px solid #dee1e8'}
            px={2}
            mx={6}
            my={1}
            borderRadius={'8px'}
            flexDirection={'row'}
            width="fit-content">
            <FormControlLabel
              control={<Checkbox style={{ padding: isSmallScreen ? 1 : undefined }} />}
              checked={preferences.shouldShowUnavailability}
              label={t('calendarOptions.hideUnavailability')}
              onChange={toggleShowUnavailability}
            />
            <FormControlLabel
              control={<Checkbox style={{ padding: isSmallScreen ? 1 : undefined }} />}
              checked={preferences.shouldShowOnlyCurrentUser}
              label={
                <Typography
                  color={preferences.shouldShowOnlyCurrentUser ? 'red' : undefined}
                  fontWeight={preferences.shouldShowOnlyCurrentUser ? 700 : undefined}>
                  {t('calendarOptions.showMyWorkshops')}
                </Typography>
              }
              onChange={toggleShouldShowOnlyCurrentUser}
            />

            <FormControlLabel
              control={<Checkbox style={{ padding: isSmallScreen ? 1 : undefined }} />}
              checked={preferences.shouldShowShifts}
              label={
                account.shifts_as_employee_unavailability
                  ? t('calendarOptions.showEmployeeUnavliability')
                  : t('calendarOptions.showShifts')
              }
              onChange={toggleShouldShowShifts}
            />
            {(!account.should_hide_user_availability_from_employees || !readOnly) && (
              <FormControlLabel
                control={<Checkbox style={{ padding: isSmallScreen ? 1 : undefined }} />}
                checked={preferences.shouldShowUserAvailabilities}
                label={t('calendarOptions.showUserAvailabilities')}
                onChange={toggleShowUserUnavailability}
              />
            )}
          </Grid>
        </FormGroup>

        <Grid item mx={6}>
          {!isSmallScreen && (
            <CapacityStatsGrid capacityStats={capacityStats} calendarView={calendarView} currentDate={currentDate} />
          )}
        </Grid>
      </Grid>
      <div className="calendar-container" id="calendar-container" onScroll={handleScroll}>
        <Box className="sticky-part">
          <div className="shadow-box" />
          {numOfDays !== 1 && (
            <div className="flex header-row">
              <Box
                className="cell first-column"
                sx={{
                  borderBottom: 'none',
                  alignItems: 'flex-end',
                  justifyContent: 'flex-end',
                }}>
                <Box
                  sx={{
                    borderBottom: '1px solid #e6e6e6',
                    width: '12px',
                    height: '50px',
                    boxSizing: 'border-box',
                  }}
                />
              </Box>
              {[...Array(numOfDays).keys()].map((numOfDay) => {
                const date = moment(currentDate).add(numOfDay, 'days');
                const dateCloned = date.clone();
                const waitingListItemsNumber = calendarItems.waitingList[dateCloned.format(DATE_FORMAT)]?.length;

                return (
                  <Grid
                    minWidth="24px"
                    pt={1}
                    container
                    flexDirection="column"
                    key={`header-${numOfDay}`}
                    sx={{
                      backgroundColor: dateCloned.format(DATE_FORMAT) === today ? '#D2D1D1' : cellBackgroundColor(date),
                      fontWeight: dateCloned.format(DATE_FORMAT) === today ? 800 : undefined,
                    }}
                    className={`cell flex-1`}
                    justifyContent="flex-start">
                    <Grid container justifyContent="center">
                      <Typography fontWeight={dateCloned.format(DATE_FORMAT) === today ? 800 : undefined}>
                        {dateCloned.clone().locale(language).format(HEADER_FORMAT_SMALL)}
                      </Typography>
                    </Grid>
                    <Typography fontSize="12px" color="grey">
                      {getHoliday(account.holiday_calendar, dateCloned.format(DATE_FORMAT), language)}
                    </Typography>
                    <WaitingListIndicator
                      date={dateCloned.format(DATE_FORMAT)}
                      amountOfItems={waitingListItemsNumber}
                    />
                    {(numOfDays === 3 || numOfDays === 7) && !isSmallScreen && (
                      <Box>
                        {(() => {
                          const dayStats = capacityStats.daily.find(
                            (day) => day.date === dateCloned.format(DATE_FORMAT),
                          );
                          if (dayStats && (dayStats.orderCount > 0 || dayStats.personCount > 0)) {
                            return (
                              <Typography variant="caption" fontSize="12px" color="text.secondary">
                                {dayStats.orderCount > 0 && `${dayStats.orderCount} ${t('capacity.orders')}`}
                                {dayStats.orderCount > 0 && dayStats.personCount > 0 && ' • '}
                                {dayStats.personCount > 0 && `${dayStats.personCount} ${t('capacity.persons')}`}
                              </Typography>
                            );
                          }
                          return null;
                        })()}
                      </Box>
                    )}
                  </Grid>
                );
              })}
            </div>
          )}
          {numOfDays === 1 && (
            <div className="flex header-row">
              <Box
                className="cell first-column"
                sx={{
                  borderBottom: 'none',
                  alignItems: 'flex-end',
                  justifyContent: 'flex-end',
                }}>
                <Box
                  className="first-column"
                  sx={{
                    borderBottom: 'none',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-end',
                  }}>
                  {numOfDays === 1 && (
                    <Grid container flexDirection="column" alignItems="center">
                      <Grid item>
                        <Typography>{moment(currentDate).locale(language).format('ddd')}</Typography>
                      </Grid>
                      <Grid item>
                        <Typography>{moment(currentDate).format('DD')}</Typography>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Box>
              <Grid
                minWidth="24px"
                pt={1}
                container
                flexDirection={'column'}
                justifyContent="flex-end"
                alignItems="center">
                <Typography fontSize="12px" color="grey">
                  {getHoliday(account.holiday_calendar, moment(currentDate).format(DATE_FORMAT), language)}
                </Typography>
                <WaitingListIndicator
                  date={currentDate}
                  amountOfItems={
                    calendarItems.waitingList[currentDate]?.filter((x) => x.status === WaitingListStatus.ACTIVE).length
                  }
                />
                {shouldShowResources && <ResourceHeaders />}
              </Grid>
            </div>
          )}
          <div className="flex body-row">
            <Box className=" first-column" />
            {[...Array(numOfDays).keys()].map((numOfDay) => {
              return (
                <div
                  key={numOfDay}
                  style={{ backgroundColor: cellBackgroundColor(moment(currentDate).add(numOfDay, 'days')) }}
                  className={` flex-1`}>
                  <AllDayCalendarCell
                    unavailabilities={
                      calendarItems.unavailabilities[moment(currentDate).add(numOfDay, 'days').format(DATE_FORMAT)]
                    }
                    notes={calendarItems.notes[moment(currentDate).add(numOfDay, 'days').format(DATE_FORMAT)]}
                    showUnavailability={preferences.shouldShowUnavailability}
                  />
                </div>
              );
            })}
          </div>
        </Box>

        <Box style={{ position: 'relative' }}>
          <div className="flex body-row last-body-row">
            <div className="cell first-column cell">
              <HoursBordersHeader account={account} />
            </div>
            {[...Array(numOfDays).keys()].map((numOfDay) => {
              return (
                <div
                  key={numOfDay}
                  className={`cell flex-1`}
                  style={{
                    backgroundColor: cellBackgroundColor(moment(currentDate).add(numOfDay, 'days')),
                    borderInlineStart: '1.5px solid black',
                  }}>
                  {isNumOfDayToday(numOfDay) && <CurrentTimeDivider />}
                  <CalendarDay
                    preferences={preferences}
                    dateToRender={moment(currentDate).add(numOfDay, 'days').format(DATE_FORMAT)}
                    items={calendarItems}
                    openOrder={openOrder}
                    openEvent={openEvent}
                    numOfDays={numOfDays}
                  />
                </div>
              );
            })}
          </div>
        </Box>
      </div>
    </>
  );
};
export default MultipleDaysView;
